import { makeAutoObservable } from 'mobx'
import { type IResponseTollFree, type IResponseTollFreeNumber } from 'entities/Compliance'
import { TollFreeProfileNumber } from 'entities/Compliance/model/OrganizationTollFree/TollFreeProfileNumber'

export class TollFreeProfile {
  private _id: IResponseTollFree['id'] | null = null
  private _title: IResponseTollFree['title'] | null = null
  private _numbersMap: Map<number, TollFreeProfileNumber> = new Map()
  private _use_case: IResponseTollFree['use_case'] | null = null
  private _completed: IResponseTollFree['completed'] = false
  private _monthly_volume: IResponseTollFree['monthly_volume'] | null = null
  private _opt_in_data: IResponseTollFree['opt_in_data'] | null = null
  private _opt_in_methods: IResponseTollFree['opt_in_methods'] | null = null
  private _step: IResponseTollFree['step'] | null = null
  privacy_policy_url: IResponseTollFree['privacy_policy_url'] = ''
  terms_url: IResponseTollFree['terms_url'] = ''
  private readonly _origin: IResponseTollFree

  constructor(response: IResponseTollFree) {
    makeAutoObservable(this)

    this._origin = response

    this.syncData(response)
  }

  syncData = (response: IResponseTollFree) => {
    this._id = response.id
    this._title = response.title
    this._completed = response.completed
    this._monthly_volume = response.monthly_volume
    this._opt_in_data = response.opt_in_data
    this._opt_in_methods = response.opt_in_methods
    this._step = response.step
    this.privacy_policy_url = response?.privacy_policy_url ?? ''
    this.terms_url = response?.terms_url ?? ''

    if (response.use_case_id) {
      this._use_case = {
        id: response.use_case_id,
        sample_message_1: response.sample_message_1 || '',
        sample_message_2: response.sample_message_2 || '',
        description: response.use_case_description || '',
        title: response.use_case?.title || '',
      }
    }
    this.addNumbers(response.numbers)
  }

  addNumbers = (numbers: IResponseTollFree['numbers']) => {
    numbers?.forEach((number) => {
      this.addNumber(number)
    })
  }

  addNumber = (number: IResponseTollFreeNumber) => {
    this._numbersMap.set(number.id, new TollFreeProfileNumber(number))
  }

  get id() {
    return this._id || 0
  }

  get completed() {
    return this._completed
  }

  get origin() {
    return this._origin
  }

  get title() {
    return this._title
  }

  get numberCount() {
    return this._numbersMap.size
  }

  get numbers() {
    return Array.from(this._numbersMap.values())
  }

  get use_case() {
    return this._use_case
  }

  get monthlyVolume() {
    return this._monthly_volume
  }

  get optInData() {
    return this._opt_in_data
  }

  get optInMethods() {
    return this._opt_in_methods
  }

  get useCaseName() {
    return this._use_case?.title || ''
  }

  get step() {
    return this._step
  }

  get continueStep() {
    if (!this.step) return 1

    const commonStepForAllOptIn = [5, 6]

    // exception for optInMethods steps. All steps should start from 5
    if (commonStepForAllOptIn.includes(this.step)) return 5

    return this.step + 1
  }

  get numbersStatusCounts() {
    return this.numbers.reduce(
      (state, current) => {
        const statusKey = current.status.toLowerCase().replace(/ /g, '_')

        state[statusKey as keyof typeof state] = (state[statusKey as keyof typeof state] || 0) + 1

        return state
      },
      {
        unverified: 0,
        denied: 0,
        blocked: 0,
        verified: 0,
        internal_review: 0,
        under_review: 0,
      }
    )
  }

  get isStatusInUnderReview() {
    return this.numbers.every((item) => item.isStatusInCarrierReview || item.isStatusPendingReview)
  }

  get isStatusInProgress() {
    return this.numbers.some((item) => item.isStatusInProgress)
  }

  get isStatusInCarrierReview() {
    return this.numbers.some((item) => item.isStatusInCarrierReview)
  }

  get isStatusVerified() {
    return this.numbers.every((item) => item.isStatusVerified)
  }

  get isStatusPendingReview() {
    return this.numbers.some((item) => item.isStatusPendingReview)
  }

  get isStatusRejected() {
    return this.numbers.some((item) => item.isStatusRejected)
  }

  get isStatusBlocked() {
    return this.numbers.some((item) => item.isStatusBlocked)
  }
}
